/**
 * Carroll's motion layer.
 *
 * Adds GSAP / ScrollTrigger driven scroll-reveals, hover lifts, and sticky
 * polish on top of the Fortis CSS bundle. Every effect is opt-out via
 * `prefers-reduced-motion` and degrades to instant visibility without GSAP.
 *
 * Triggers:
 *   - `[data-motion="reveal"]` (or one of the auto-tagged Carroll's surfaces):
 *     fade + 24px translateY in on viewport entry.
 *   - `[data-motion="reveal-stagger"]` children fade in sequentially.
 *   - `.product-item_component`: subtle hover lift handled by CSS, but we
 *     wire pointermove-driven image scale via a class toggle.
 *
 * Surfaces auto-tagged on PDP / Home / Collection so authors don't have to
 * edit Webflow markup just to get motion. Anything already carrying
 * `data-motion` is respected.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var REVEAL_SELECTORS = [
    '.product-header_gallery-grid-item',
    '.product-header_accordion-item',
    '.product-header_we-got-you-item',
    '.product-item_component',
    '.section_product-rec',
    '.section_recently-viewed',
    '.section_faq-1',
    '.section_reviews',
    '.section_home-hero',
    '.section_ethos',
    '.section_highlight',
    '.section_category',
    '.section_collection-hero',
    '.collection_products-list-item',
    '.default_heading',
  ];

  function tagReveals(root) {
    REVEAL_SELECTORS.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) {
        if (!el.hasAttribute('data-motion')) el.setAttribute('data-motion', 'reveal');
      });
    });
  }

  function initStaticFallback() {
    document.querySelectorAll('[data-motion]').forEach(function (el) {
      el.style.opacity = '';
      el.style.transform = '';
    });
  }

  function initGsap() {
    if (!window.gsap) return false;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

    var reveals = document.querySelectorAll('[data-motion="reveal"]');
    reveals.forEach(function (el, index) {
      gsap.set(el, { autoAlpha: 0, y: 24 });
      var animation = {
        autoAlpha: 1,
        y: 0,
        duration: 0.7,
        ease: 'power2.out',
        delay: Math.min(index * 0.015, 0.18),
      };
      if (ScrollTrigger) {
        gsap.to(el, Object.assign({}, animation, {
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
            once: true,
          },
        }));
      } else {
        gsap.to(el, animation);
      }
    });

    var staggers = document.querySelectorAll('[data-motion="reveal-stagger"]');
    staggers.forEach(function (container) {
      var children = container.querySelectorAll(':scope > *');
      if (!children.length) return;
      gsap.set(children, { autoAlpha: 0, y: 18 });
      var animation = {
        autoAlpha: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out',
        stagger: 0.08,
      };
      if (ScrollTrigger) {
        gsap.to(children, Object.assign({}, animation, {
          scrollTrigger: { trigger: container, start: 'top 85%', once: true },
        }));
      } else {
        gsap.to(children, animation);
      }
    });

    return true;
  }

  function hoverLifts() {
    document.querySelectorAll('.product-item_component').forEach(function (card) {
      card.addEventListener('pointerenter', function () { card.classList.add('is-hovered'); });
      card.addEventListener('pointerleave', function () { card.classList.remove('is-hovered'); });
    });
  }

  function injectStyles() {
    if (document.getElementById('carrols-motion-styles')) return;
    var style = document.createElement('style');
    style.id = 'carrols-motion-styles';
    style.textContent = [
      '.product-item_component { transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1); will-change: transform; }',
      '.product-item_component.is-hovered { transform: translateY(-4px); }',
      '.product-item_image { transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1); }',
      '.product-item_component.is-hovered .product-item_image { transform: scale(1.035); }',
      '[data-motion] { will-change: opacity, transform; }',
      '@media (prefers-reduced-motion: reduce) {',
      '  [data-motion] { opacity: 1 !important; transform: none !important; }',
      '  .product-item_component, .product-item_image { transition: none !important; transform: none !important; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  function init() {
    injectStyles();
    tagReveals(document);
    if (REDUCED) {
      initStaticFallback();
      hoverLifts();
      return;
    }
    var ok = initGsap();
    if (!ok) initStaticFallback();
    hoverLifts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
